import { useState, type FormEvent } from 'react';
import { Button, Modal } from '@buildmate/ui-web';
import { createJob, JobValidationError, systemClock, type CreateJobInput } from '@buildmate/core';
import { EMPTY_JOB_FORM, JobFields, type JobFormValues } from './JobFields.tsx';
import { usePorts } from '../lib/ports-context.tsx';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly companyId: string;
  readonly companyTimeZone: string;
  readonly uid: string;
  readonly onCreated: () => void;
}

export function NewJobDialog({ open, onClose, companyId, companyTimeZone, uid, onCreated }: Props) {
  const ports = usePorts();
  const [values, setValues] = useState<JobFormValues>(EMPTY_JOB_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof JobFormValues) => (event: { target: { value: string } }) =>
    setValues((previous) => ({ ...previous, [key]: event.target.value }));

  function close() {
    setValues(EMPTY_JOB_FORM);
    setFieldErrors({});
    setFormError(null);
    onClose();
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFieldErrors({});
    setFormError(null);

    const input: CreateJobInput = {
      name: values.name,
      prefix: values.prefix,
      status: values.status as CreateJobInput['status'],
      address: {
        line1: values.line1,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
        country: 'US',
      },
      contractType: values.contractType as CreateJobInput['contractType'],
      ...(values.startDate ? { startDate: values.startDate } : {}),
      ...(values.projectedEndDate ? { projectedEndDate: values.projectedEndDate } : {}),
    };

    try {
      await createJob(
        { data: ports.data, clock: systemClock, companyId, uid, companyTimeZone },
        input,
      );
      close();
      onCreated();
    } catch (cause) {
      if (cause instanceof JobValidationError) {
        setFieldErrors(cause.fieldErrors);
      } else {
        // Users get a plain sentence; the underlying error still reaches the
        // console, because a generic message that hides the cause makes this
        // class of failure very hard to diagnose.
        console.error('[buildmate] createJob failed', cause);
        setFormError(
          'Could not save the job. You may not have permission to create jobs in this company.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="New job"
      subtitle="Every schedule, log and invoice hangs off a job"
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close} type="button">
            Cancel
          </Button>
          <Button type="submit" form="new-job-form" loading={busy}>
            Create job
          </Button>
        </>
      }
    >
      <form id="new-job-form" onSubmit={onSubmit} className="flex flex-col gap-4">
        {formError && (
          <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">
            {formError}
          </p>
        )}

        <JobFields values={values} onChange={set} fieldErrors={fieldErrors} prefix="editable" />
      </form>
    </Modal>
  );
}
